import { App, Skeleton } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { assistantApi, useCreateAssistantMutation } from '@entities/assistant';
import { answerPrice, useModels, type LlmModel } from '@entities/llm';
import { useUserStore } from '@entities/user';
import { readDraft, writeDraft } from '@features/create-assistant';
import { extractErrorMessage, httpClient } from '@shared/api';
import { TopBar, num, plural, rub, shortWhen } from '@shared/ui/v2';
import './api.css';

interface Access {
  assistant_id: string;
  name: string;
  model_title: string | null;
  model_slug: string | null;
  sources: number;
  requests_30d: number;
  key_last4: string | null;
  key_active: boolean;
  key_created_at: string | null;
  last_used_at: string | null;
}

const KEY = ['api-access'];
const BASE_URL = 'https://cabinet.richat.ru/api/v1';
const FORM_KEY = 'richat.apiForm';

type Form = { name: string; prompt: string; kb: 'none' | 'files' };
const EMPTY: Form = { name: '', prompt: '', kb: 'none' };

function readForm(): Form {
  try {
    const raw = window.sessionStorage.getItem(FORM_KEY);
    return raw ? { ...EMPTY, ...(JSON.parse(raw) as Partial<Form>) } : { ...EMPTY };
  } catch {
    return { ...EMPTY };
  }
}
function writeForm(f: Form | null) {
  try {
    if (f) window.sessionStorage.setItem(FORM_KEY, JSON.stringify(f));
    else window.sessionStorage.removeItem(FORM_KEY);
  } catch {
    /* только в памяти */
  }
}

const CAPS = [
  'Текст и диалоги',
  'Картинки',
  'Файлы и PDF',
  'Ответ по мере генерации',
  'Вызов ваших функций',
  'Ответ в JSON',
  'Температура и длина',
];

function modelLabel(m: LlmModel) {
  const p = answerPrice(m);
  const parts = [m.title];
  if (m.data_in_russia) parts.push('данные в России');
  const head = parts.join(' — ');
  return p.value != null ? `${head} · ~${rub(p.value)} за ответ` : head;
}

function examples(model: string) {
  const py = `from openai import OpenAI

client = OpenAI(base_url="${BASE_URL}",
                api_key=RICHAT_KEY)

r = client.chat.completions.create(
    model="${model}",   # любая модель каталога;
                        # без model — модель ассистента
    messages=[{"role": "user", "content": [
        {"type": "text", "text": "Что сломалось? Фото ошибки:"},
        {"type": "image_url", "image_url": {"url": photo_url}},
    ]}],
)
print(r.choices[0].message.content)`;
  const curl = `curl ${BASE_URL}/chat/completions \\
  -H "Authorization: Bearer $RICHAT_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "messages": [{"role": "user", "content": "Сколько стоит диагностика?"}]
  }'`;
  const node = `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "${BASE_URL}",
  apiKey: process.env.RICHAT_KEY,
});

const r = await client.chat.completions.create({
  model: "${model}", // любая модель каталога; без model — модель ассистента
  messages: [{ role: "user", content: "Сколько стоит диагностика?" }],
});
console.log(r.choices[0].message.content);`;
  return { Python: py, cURL: curl, 'Node.js': node } as const;
}

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Экраны 12–13 «API» (утверждены 28.10.2026): доступы, новый доступ, как подключить. */
export function ApiPage() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const { data: models } = useModels();
  const create = useCreateAssistantMutation();
  const { data: list, error } = useQuery<Access[], Error>({
    queryKey: KEY,
    queryFn: async () => (await httpClient.get<Access[]>('/api-access')).data,
  });

  const [form, setForm] = useState<Form>(readForm);
  // Модель: из «Сравнить модели» (экран выбора пишет её в черновик создания).
  const [modelId, setModelId] = useState<number | null>(() => readDraft().modelId);
  const [nameError, setNameError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [improving, setImproving] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [fresh, setFresh] = useState<{ aid: string; key: string; kb: boolean } | null>(null);
  const [tab, setTab] = useState<'Python' | 'cURL' | 'Node.js'>('Python');
  const [existing, setExisting] = useState('');
  // Ассистент создан, а ключ выдать не удалось — повтор только выдаёт ключ, без второго ассистента.
  const [createdAid, setCreatedAid] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => writeForm(form), [form]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const all = models ?? [];
  const sorted = [...all].sort(
    (a, b) =>
      // Сначала модели Yandex, затем Рег.облака, затем остальные (решение 31.10).
      (a.group ?? 2) - (b.group ?? 2) ||
      Number(b.is_default) - Number(a.is_default) ||
      Number(b.verified) - Number(a.verified) ||
      a.title.localeCompare(b.title, 'ru'),
  );
  const defId = all.find((m) => m.is_default)?.id ?? sorted[0]?.id ?? null;
  const curModelId = modelId ?? defId;

  const accesses = list ?? [];
  const current = accesses.find((a) => a.assistant_id === selected) ?? accesses[0] ?? null;
  // В примере — модель доступа, если она видит картинки (пример с фото); иначе — рекомендованная с vision.
  const curModel = all.find((m) => m.slug === current?.model_slug);
  const visionModel = curModel?.supports_vision
    ? curModel
    : (all.find((m) => m.verified && m.supports_vision) ?? all.find((m) => m.supports_vision));
  const ex = examples(visionModel?.slug ?? current?.model_slug ?? 'anthropic/claude-haiku-5.5');
  const withKey = new Set(accesses.map((a) => a.assistant_id));
  const others = (user?.assistants ?? []).filter((a) => !withKey.has(a.id));

  const issue = async (aid: string, kb = false) => {
    const { data } = await httpClient.post<{ key: string; last4: string }>(
      `/api-access/${aid}/key`,
    );
    setFresh({ aid, key: data.key, kb });
    setSelected(aid);
    await qc.invalidateQueries({ queryKey: KEY });
    return data;
  };

  const improve = async () => {
    if (!form.name.trim()) {
      setNameError(true);
      nameRef.current?.focus();
      message.info('Сначала впишите название');
      return;
    }
    if (form.prompt.trim().length < 3) {
      message.info('Опишите задачу в паре фраз — ИИ превратит это в инструкцию');
      return;
    }
    setImproving(true);
    try {
      const p = await assistantApi.composeInstruction({
        name: form.name.trim(),
        description: form.prompt.trim(),
        model_id: curModelId ?? undefined,
      });
      set('prompt', p);
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setImproving(false);
    }
  };

  const submit = async () => {
    if (busy) return;
    if (!form.name.trim()) {
      setNameError(true);
      nameRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      let aid = createdAid;
      if (!aid) {
        const a = await create.mutateAsync({
          name: form.name.trim(),
          ...(form.prompt.trim() ? { prompt: form.prompt.trim() } : {}),
          ...(curModelId ? { model_id: curModelId } : {}),
        });
        aid = a.id;
        setCreatedAid(aid);
      }
      await issue(aid, form.kb === 'files');
      setCreatedAid(null);
      setForm({ ...EMPTY });
      writeForm(null);
      setModelId(null);
      writeDraft({ ...readDraft(), modelId: null });
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const issueExisting = async () => {
    if (!existing) return;
    setBusy(true);
    try {
      await issue(existing);
      setExisting('');
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const reissue = (a: Access) =>
    modal.confirm({
      title: 'Перевыпустить ключ?',
      content: `Старый ключ «${a.name}» перестанет работать сразу — замените его в своих программах.`,
      okText: 'Перевыпустить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await issue(a.assistant_id);
        } catch (e) {
          message.error(extractErrorMessage(e));
        }
      },
    });

  const revoke = (a: Access) =>
    modal.confirm({
      title: 'Отключить API?',
      content: `Ключ «${a.name}» перестанет работать. Ассистент, его база знаний и каналы останутся.`,
      okText: 'Отключить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await httpClient.delete(`/api-access/${a.assistant_id}/key`);
          if (fresh?.aid === a.assistant_id) setFresh(null);
          if (selected === a.assistant_id) setSelected(null);
          await qc.invalidateQueries({ queryKey: KEY });
        } catch (e) {
          message.error(extractErrorMessage(e));
        }
      },
    });

  const freshAccess = fresh ? accesses.find((a) => a.assistant_id === fresh.aid) : null;

  return (
    <>
      <TopBar
        title="API"
        hint="300+ ИИ моделей для ваших задач — через вашего именного ассистента. База знаний не обязательна."
        actions={
          <button
            type="button"
            className="v2-btn is-primary"
            onClick={() => {
              nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              nameRef.current?.focus();
            }}
          >
            Новый доступ
          </button>
        }
      />
      <div className="v2-content">
        {error ? (
          <div className="v2-card v2-card-pad">{extractErrorMessage(error)}</div>
        ) : !list ? (
          <div className="v2-card v2-card-pad">
            <Skeleton active paragraph={{ rows: 2 }} />
          </div>
        ) : accesses.length > 0 ? (
          <section className="v2-card" aria-labelledby="ap-list">
            <div className="ap-card-head">
              <h2 id="ap-list" className="v2-h2">
                Доступы
              </h2>
            </div>
            <div className="v2-table-wrap">
              <table className="v2-table ap-table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Модель</th>
                    <th>База знаний</th>
                    <th>Запросов за 30 дней</th>
                    <th>Ключ</th>
                  </tr>
                </thead>
                <tbody>
                  {accesses.map((a) => (
                    <tr
                      key={a.assistant_id}
                      className={current?.assistant_id === a.assistant_id ? 'is-on' : undefined}
                      onClick={() => setSelected(a.assistant_id)}
                    >
                      <td>
                        <button
                          type="button"
                          className="ap-row-btn"
                          onClick={() => setSelected(a.assistant_id)}
                        >
                          {a.name}
                        </button>
                        <div className="v2-xs v2-muted">
                          <Link
                            to={`/assistants/${a.assistant_id}/settings`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            настройка ассистента
                          </Link>
                        </div>
                      </td>
                      <td>{a.model_title ?? '—'}</td>
                      <td>
                        {a.sources > 0
                          ? `${a.sources} ${plural(a.sources, 'источник', 'источника', 'источников')}`
                          : 'не нужна'}
                      </td>
                      <td>{num(a.requests_30d)}</td>
                      <td>
                        <span className="ap-badge">активен</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        <div className="ap-grid">
          <section className="v2-card ap-card" aria-labelledby="ap-new">
            <h2 id="ap-new" className="v2-h2 ap-h">
              Новый доступ
            </h2>
            <label className="ap-field">
              <span className="ap-label">
                Название ассистента <span className="ap-req">*</span>
              </span>
              <input
                ref={nameRef}
                className={nameError ? 'ap-input is-error' : 'ap-input'}
                value={form.name}
                maxLength={200}
                aria-invalid={nameError}
                placeholder="Например, «Помощник отдела продаж»"
                onChange={(e) => {
                  set('name', e.target.value);
                  setNameError(false);
                }}
              />
              <span className={nameError ? 'v2-xs ap-err' : 'v2-xs v2-muted'}>
                Обязательно: API работает от имени вашего ассистента, ключ привязан к нему.
              </span>
            </label>

            <div className="ap-field">
              <div className="ap-row">
                <label htmlFor="ap-prompt" className="ap-label-row">
                  <b>Инструкция</b>{' '}
                  <span className="v2-muted">
                    — необязательно, можно передавать свою в каждом запросе
                  </span>
                </label>
                <button
                  type="button"
                  className="v2-btn ap-ai"
                  onClick={improve}
                  disabled={improving}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    aria-hidden="true"
                  >
                    <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" />
                  </svg>
                  {improving ? 'ИИ думает…' : 'Улучшить с помощью ИИ'}
                </button>
              </div>
              <textarea
                id="ap-prompt"
                className="ap-input ap-textarea"
                rows={4}
                maxLength={8000}
                value={form.prompt}
                placeholder="Например: «Классифицируй заявку клиента по теме и кратко перескажи её для менеджера»"
                onChange={(e) => set('prompt', e.target.value)}
              />
            </div>

            <div className="ap-field">
              <div className="ap-row">
                <label htmlFor="ap-model" className="ap-label-row">
                  <b>Модель по умолчанию</b>{' '}
                  <span className="v2-muted">— в запросе можно указать любую</span>
                </label>
                <Link to="/assistants/new/model" className="v2-link">
                  Сравнить модели
                </Link>
              </div>
              <select
                id="ap-model"
                className="ap-input"
                value={curModelId ?? ''}
                onChange={(e) => setModelId(Number(e.target.value) || null)}
              >
                {sorted.map((m) => (
                  <option key={m.id} value={m.id}>
                    {modelLabel(m)}
                  </option>
                ))}
              </select>
            </div>

            <div className="ap-field" role="radiogroup" aria-label="База знаний">
              <span className="ap-label">База знаний</span>
              {[
                {
                  id: 'none' as const,
                  t: 'Не нужна',
                  h: 'Модель работает только по инструкции и данным из запроса',
                },
                {
                  id: 'files' as const,
                  t: 'Загрузить документы',
                  h: 'Ответы по вашим файлам и сайту — можно и позже',
                },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={form.kb === o.id}
                  className={form.kb === o.id ? 'ap-choice is-on' : 'ap-choice'}
                  onClick={() => set('kb', o.id)}
                >
                  <span className="ap-radio" aria-hidden="true" />
                  <span>
                    <span className="ap-choice-t">{o.t}</span>
                    <span className="v2-xs v2-muted">{o.h}</span>
                  </span>
                </button>
              ))}
            </div>

            <button type="button" className="ap-submit" onClick={submit} disabled={busy}>
              {busy ? 'Создаём…' : createdAid ? 'Получить ключ ещё раз' : 'Создать и получить ключ'}
            </button>

            {others.length > 0 && (
              <div className="ap-existing">
                <span className="v2-small v2-muted">Или ключ для готового ассистента:</span>
                <select
                  className="ap-input ap-existing-sel"
                  value={existing}
                  aria-label="Ассистент"
                  onChange={(e) => setExisting(e.target.value)}
                >
                  <option value="">Выберите ассистента</option>
                  {others.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="v2-btn"
                  onClick={issueExisting}
                  disabled={!existing || busy}
                >
                  Получить ключ
                </button>
              </div>
            )}
          </section>

          <section className="v2-card ap-card" aria-labelledby="ap-how">
            <h2 id="ap-how" className="v2-h2 ap-h">
              Как подключить
            </h2>
            <p className="v2-small ap-p">
              Совместимо с OpenAI API: в любой библиотеке или программе замените адрес и ключ — и
              пользуйтесь всеми возможностями выбранной модели.
            </p>
            <div className="ap-chips">
              {CAPS.map((c) => (
                <span key={c} className="ap-chip">
                  {c}
                </span>
              ))}
            </div>
            <p className="v2-xs v2-muted ap-p">
              Доступно то, что умеет выбранная модель — смотрите в карточке модели.
            </p>

            {fresh && freshAccess && (
              <div className="ap-fresh" role="status">
                <div className="ap-fresh-t">
                  Ключ «{freshAccess.name}» создан — скопируйте его сейчас
                </div>
                <div className="v2-xs">
                  Потом покажем только последние 4 символа. Потеряете — перевыпустите.
                </div>
                <div className="ap-fresh-row">
                  <code className="ap-fresh-key">{fresh.key}</code>
                  <button
                    type="button"
                    className="v2-btn"
                    onClick={async () =>
                      (await copy(fresh.key))
                        ? message.success('Ключ скопирован')
                        : message.info('Выделите ключ и скопируйте вручную')
                    }
                  >
                    Скопировать
                  </button>
                </div>
                {fresh.kb && (
                  <button
                    type="button"
                    className="v2-btn ap-fresh-kb"
                    onClick={() => navigate(`/assistants/${fresh.aid}/knowledge?upload=1`)}
                  >
                    Загрузить документы →
                  </button>
                )}
              </div>
            )}

            {current && (
              <div className="ap-key">
                <div style={{ minWidth: 0 }}>
                  <div className="v2-xs v2-muted">
                    Ключ «{current.name}» · последний запрос:{' '}
                    {current.last_used_at ? shortWhen(current.last_used_at) : 'ещё не было'}
                  </div>
                  <code className="ap-key-mask">rch_live_••••••••••••{current.key_last4}</code>
                </div>
                <div className="ap-key-btns">
                  <button type="button" className="v2-btn" onClick={() => reissue(current)}>
                    Перевыпустить
                  </button>
                  <button type="button" className="v2-btn ap-off" onClick={() => revoke(current)}>
                    Отключить
                  </button>
                </div>
              </div>
            )}

            <div className="ap-tabs" role="tablist" aria-label="Пример кода">
              {(Object.keys(ex) as Array<keyof typeof ex>).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  className={tab === t ? 'ap-tab is-on' : 'ap-tab'}
                  onClick={() => setTab(t)}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="ap-code-wrap">
              <pre className="ap-code">{ex[tab]}</pre>
              <button
                type="button"
                className="ap-code-copy"
                onClick={async () => (await copy(ex[tab])) && message.success('Пример скопирован')}
              >
                Копировать
              </button>
            </div>
            <p className="v2-small ap-p">
              <b>Ассистент:</b> если в запросе нет своей инструкции — берётся инструкция ассистента;
              база знаний подключается, если загружена.
            </p>
            <p className="v2-small ap-p">
              <b>Оплата</b> по токенам выбранной модели, с общего баланса; при нуле — ошибка 402. Не
              больше 300 запросов в минуту на аккаунт.
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
